import { Canvas } from "@react-three/fiber";
import React, { Suspense, useEffect, useState } from "react";
import Door from "./Door";
import { Vector3 } from "three";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import Loader from "../../../shared/ui/loader";
import Foreground from "./Foreground";
import Background from "./Background";
import { twMerge } from "tailwind-merge";
import { BakeShadows, Environment, Preload } from "@react-three/drei";
import { getFullAssetPath } from "shared/utils/assets";
import { defaultDoorWidth } from "shared/utils/doorSceneConstants";
import { newTextureXLXLDoors } from "features/option-controls/door-width-controls/XLXXLHelper";

interface DoorProps {
  className?: string;
  doorClassName?: string;
}

const MainProductScene = ({ className = "", doorClassName }: DoorProps) => {
  const { selectedDoor, eigenwijs, getConfigurationWidth } =
    useConfigurationOptionsContext();
  const [dpr, setDpr] = useState(1.5);
  const [defaultDoorPosition, setDefaultDoorPosition] = useState<Vector3>(
    new Vector3(0, 0, 0),
  );
  const [doorPosition, setDoorPosition] = useState<Vector3>(
    new Vector3(0, 0, 0),
  );
  const [doorPositionXOffset, setDoorPositionXOffset] = useState<number>(0);
  const [doorScale, setDoorScale] = useState<Vector3>(new Vector3(0, 0, 0));

  const isDoorXLXXL =
    getConfigurationWidth(0) >= 1151 &&
    newTextureXLXLDoors.includes(selectedDoor?.code ?? "");
  useEffect(() => {
    if (selectedDoor && selectedDoor.configuration.nElements) {
      let position: Vector3;
      let scale: Vector3;

      if (selectedDoor.code.includes("schuifdeur")) {
        const singleConfigurationBiggerScene = ["schuifdeur"];
        const doubleConfigurationSmallerScene = ["schuifdeur1paneel"];
        const doubleConfigurationsBiggerScene = ["dubbeleschuifdeur"];
        const twoHalfElements = [
          "schuifdeur2paneel",
          "schuifdeurpaneelschuifdeur",
        ];
        const threeElements = ["dubbeleschuifdeur1paneel"];
        const threeElementsCenter = ["schuifdeurpaneelpaneel"];
        const fourElements = [
          "dubbeleschuifdeur2paneel",
          "schuifdeurschuifdeurpaneelpaneel",
          "schuifdeurpaneelpaneelschuifdeur",
        ];

        if (singleConfigurationBiggerScene.includes(selectedDoor.code)) {
          // Old configuration
          // position = new Vector3(-0.45, -1.22, -0.2);
          // scale = new Vector3(0.98, 0.98, 1);
          position = new Vector3(-0.5, -0.85, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        } else if (
          doubleConfigurationSmallerScene.includes(selectedDoor.code)
        ) {
          position = new Vector3(0, -1.2, -0.2);
          scale = new Vector3(0.98, 0.98, 1);
        } else if (
          doubleConfigurationsBiggerScene.includes(selectedDoor.code)
        ) {
          position = new Vector3(-0.01, -0.58, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        } else if (twoHalfElements.includes(selectedDoor.code)) {
          position = new Vector3(-0.3, -0.58, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        } else if (threeElements.includes(selectedDoor.code)) {
          position = new Vector3(0, -0.58, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        } else if (threeElementsCenter.includes(selectedDoor.code)) {
          position = new Vector3(-0.3, -0.58, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        } else if (fourElements.includes(selectedDoor.code)) {
          const positionX = isDoorXLXXL ? -0.69 : -0.69;
          position = new Vector3(positionX, -0.58, -0.2);

          const scaleX = isDoorXLXXL ? 0.62 : 0.685;
          scale = new Vector3(scaleX, 0.685, 1);
        } else {
          position = new Vector3(-0.69, -0.58, -0.2);
          scale = new Vector3(0.685, 0.685, 1);
        }

        setDefaultDoorPosition(position);
        setDoorPosition(position);
        setDoorScale(scale);

        return;
      }

      if (selectedDoor.code.includes("trap")) {
        position = new Vector3(-0.28, -1.1, -0.2);
        setDefaultDoorPosition(position);
        setDoorPosition(position);
        setDoorScale(new Vector3(0.82, 0.82, 1));
        return;
      }

      if (selectedDoor.configuration.nElements <= 2) {
        position = new Vector3(0, -1.2, -0.2);
        scale = new Vector3(1, 1, 1);
      } else if (selectedDoor.configuration.nElements === 3) {
        position = new Vector3(-0.3, -0.58, -0.2);
        scale = new Vector3(0.685, 0.685, 1);
      } else {
        const positionX = isDoorXLXXL ? -0.69 : -0.71;
        position = new Vector3(positionX, -0.58, -0.2);

        const scaleX = isDoorXLXXL ? 0.62 : 0.686;
        scale = new Vector3(scaleX, 0.686, 1);
      }

      if (selectedDoor.code === "dubbel" && isDoorXLXXL) {
        position = new Vector3(-0.089, -0.58, -0.2);
        scale = new Vector3(0.685, 0.685, 1);
      }

      setDefaultDoorPosition(position);
      setDoorPosition(position);
      setDoorScale(scale);
    }
  }, [selectedDoor, isDoorXLXXL]);

  useEffect(() => {
    if (selectedDoor && selectedDoor.configuration.nElements) {
      setDoorPosition(
        updateDoorPosition(
          selectedDoor.configuration.nElements,
          defaultDoorPosition,
        ),
      );
    }
  }, [defaultDoorPosition, eigenwijs?.sizeObjects, selectedDoor]);

  const updateDoorPosition = (elements: number, position: Vector3) => {
    if (elements <= 2) return position;

    let xOffset = 0;

    eigenwijs?.sizeObjects.forEach((value, index) => {
      let lessOffset: number;
      let moreOffset: number;

      switch (index) {
        case 0:
          lessOffset = elements > 3 ? 0.08 : 0.06;
          moreOffset = elements > 3 ? 0.04 : 0.03;
          break;
        case 1:
        case 2:
        case 3:
          lessOffset = elements > 3 ? 0.09 : 0.1;
          moreOffset = elements > 3 ? 0.03 : 0.025;
          break;
        default:
          lessOffset = 0;
          moreOffset = 0;
      }

      if (value.customWidth < defaultDoorWidth) {
        let offset =
          (Math.abs(value.customWidth - defaultDoorWidth) / 320) * lessOffset;
        xOffset = index === 0 ? xOffset - offset : xOffset + offset;
      } else if (value.customWidth > defaultDoorWidth) {
        let offset =
          (Math.abs(value.customWidth - defaultDoorWidth) / 180) * moreOffset;
        xOffset = index === 0 ? xOffset + offset : xOffset - offset;
      }
    });

    setDoorPositionXOffset(xOffset);

    return new Vector3(position.x + xOffset, position.y, position.z);
  };

  const doorPositionXExtraOffset = isDoorXLXXL ? -0.03 : 0;
  return (
    <div className={twMerge("gg-grid gg-grid-cols-12 lg:gg-gap-2", className)}>
      <div className={twMerge(doorClassName)} data-testid="door-scene">
        <Canvas
          // dpr={dpr}
          shadows
          gl={{
            preserveDrawingBuffer: true,
            toneMappingExposure: 0.7,
          }}
          camera={{
            position: [0, 0, 2.4],
          }}
        >
          <ambientLight intensity={0.2} color={"#e6b800"} />

          {/* <PerformanceMonitor
            onIncline={() => setDpr(2)}
            onDecline={() => setDpr(1)}
          /> */}

          <Suspense fallback={<Loader />}>
            <Suspense fallback={<Loader />}>
              <Environment
                // ground={{
                // 	height: 15, // Height of the camera that was used to create the env map (Default: 15)
                // 	radius: 60, // Radius of the world. (Default 60)
                // 	scale: 400, // Scale of the backside projected sphere that holds the env texture (Default: 1000)
                // }}
                files={getFullAssetPath(
                  "/objects-v3/reading_room_environment.exr",
                )}
              />
            </Suspense>

            {/* <Center> */}
            <Foreground
              door={selectedDoor}
              doorPositionXOffset={
                doorPositionXOffset + doorPositionXExtraOffset
              }
              doorScale={doorScale}
            />

            <Background door={selectedDoor} />

            <Door
              door={selectedDoor?.code}
              position={doorPosition}
              scale={doorScale}
            />
            {/* </Center> */}
            <Preload all />
            <BakeShadows />
          </Suspense>
        </Canvas>
      </div>
    </div>
  );
};
export default MainProductScene;
